/**
 * SELEZIONE DEL COMMITTENTE — l'header `X-Portal-Access` (BE P3/P8, `v1.147.0`).
 *
 * Una persona può avere **più accessi di portale**: lo stesso referente può essere cliente di
 * due società del gruppo, e da P8 può anche essere dipendente in un'azienda e cliente in
 * un'altra. `/me` li elenca tutti in `accesses[]`; quale sia quello attivo lo decide il client
 * con questo header, e il server ne verifica la proprietà a ogni richiesta.
 *
 * Tre proprietà del contratto che spiegano perché il codice è fatto così:
 *
 *  1. **L'header vale solo dentro `/api/client-portal/*`.** Fuori dal prefisso non viene
 *     ignorato: è **rifiutato** con `403 CLIENT_PORTAL_MODE_MISMATCH`. Non è una pignoleria del
 *     server — è la garanzia che il contesto cliente non possa, per nessun percorso, influenzare
 *     una richiesta interna. Quindi va aggiunto nel punto in cui si chiama il portale, mai in
 *     un interceptor globale.
 *  2. **Un id altrui o inventato è `403 CLIENT_PORTAL_ACCESS_INVALID`, mai un ripiego
 *     silenzioso.** Una selezione rimasta in `localStorage` dopo la revoca dell'accesso
 *     bloccherebbe il portale a ogni chiamata: per questo il 403 la cancella.
 *  3. **Il passaggio fra i due mondi non richiede un token nuovo**: entrare come cliente è
 *     mandare l'header, tornare dipendente è smettere di mandarlo.
 */
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';

import { leggiChiave, writeKey, removeKey } from '@/shared/lib/storage';
import { STORAGE_KEYS } from '@/shared/lib/constants';
/** Separata dalle chiavi del gestionale: sono due sessioni diverse sullo stesso browser. */
const STORAGE_KEY = STORAGE_KEYS.portalAccessId;

/** L'accesso scelto, o `null` = «lascia decidere al server» (il suo default). */
export function selectedAccess(): string | null {
  const v = leggiChiave(STORAGE_KEY);
  return v && v.trim() !== '' ? v : null;
}

export function selectAccess(id: string | null): void {
  /* Storage negato (Safari privato): `scriviChiave` non solleva e si resta sul default del
     server, che è il comportamento giusto — non una perdita. */
  if (id) writeKey(STORAGE_KEY, id);
  else removeKey(STORAGE_KEY);
}

/**
 * Gli header da aggiungere a una chiamata del portale. Vuoto quando nessun accesso è stato
 * scelto: mandare l'header col valore che il server userebbe comunque non aggiunge niente e
 * moltiplica i modi in cui può sbagliarsi.
 */
export function portalAccessHeaders(): Record<string, string> {
  const id = selectedAccess();
  return id ? { 'X-Portal-Access': id } : {};
}

/**
 * `true` se l'errore dice che la selezione non è (più) valida — accesso revocato, cancellato,
 * o di un'altra persona. Il chiamante deve **dimenticare** la selezione: tenerla vorrebbe dire
 * un portale che resta rotto fino allo svuotamento manuale della cache del browser.
 */
export function rejectedSelection(e: unknown): boolean {
  return e instanceof JsonApiHttpError && e.getApiCode() === 'CLIENT_PORTAL_ACCESS_INVALID';
}
